import { SymbolClient } from '../grpc/generated/symbol_grpc_web_pb';
import { SymbolName, TickRequest } from '../grpc/generated/symbol_pb';
import { serverApiConfig } from '../config';
import emitter from '../utilities/emitter';
import { initializeWorker } from '../worker/serviceWorker';
import { useAppDispatch } from '../redux/app';
import { setBidAsk } from '../redux/reducer/bidask';
import { setOnTickReady } from '../redux/reducer/checkOntick';
import { getGrpcResponse } from '../utilities/getGrpcResponse';
import { metadata as getMetadata } from './metadata';
import { setTradeEventStatus } from '../redux/reducer/event';

class TickService {
  symbolClient;
  call;
  errorCount = null;
  isErrorConnect = false;
  activeSymbol = '';
  dispatch;
  reconnectParams = null;
  onlineListener = null;
  static RETRY_LIMIT = 3;
  static RETRY_DELAY = 5000;

  constructor(dispatch) {
    this.dispatch = dispatch;
    this.symbolClient = this.createSymbolClient();
    this.setupConnectivityListeners();
  }

  setupConnectivityListeners() {
    // Listen for online event
    this.onlineListener = () => {
      console.log('Internet connection restored');
      if (this.isErrorConnect && this.reconnectParams) {
        console.log('Attempting to reconnect...');
        this.attemptReconnect();
      }
    };
    
    // Listen for visibility change (tab/window hidden/visible)
    this.visibilityListener = () => {
      console.log('OnTick: Visibility changed to:', document.visibilityState);
      if (document.visibilityState === 'visible') {
        console.log('OnTick: Page became visible, checking connection...');
        // Wait a bit for network to stabilize after wake
        setTimeout(() => {
          this.checkAndReconnect();
        }, 1000);
      } else {
        console.log('OnTick: Page became hidden');
      }
    };
    
    // Listen for page resume (wake from sleep)
    this.resumeListener = () => {
      console.log('System resumed from sleep, checking connection...');
      setTimeout(() => {
        this.checkAndReconnect();
      }, 1000);
    };
    
    window.addEventListener('online', this.onlineListener);
    document.addEventListener('visibilitychange', this.visibilityListener);
    window.addEventListener('resume', this.resumeListener);
    
    // Also listen for focus event as backup
    this.focusListener = () => {
      if (this.lastActivityTime && Date.now() - this.lastActivityTime > 60000) {
        console.log('Window focus after long inactivity, checking connection...');
        this.checkAndReconnect();
      }
      this.lastActivityTime = Date.now();
    };
    window.addEventListener('focus', this.focusListener);
    
    // Track last activity
    this.lastActivityTime = Date.now();
  }

  checkAndReconnect() {
    console.log('OnTick checkAndReconnect called', {
      hasCall: !!this.call,
      isErrorConnect: this.isErrorConnect,
      hasReconnectParams: !!this.reconnectParams,
      activeSymbol: this.activeSymbol
    });

    // If we don't have reconnect params yet, can't reconnect
    if (!this.reconnectParams) {
      console.log('OnTick: No reconnect params available, skipping reconnect');
      return;
    }

    // If we have reconnect params and are in error state, reconnect
    if (this.isErrorConnect) {
      console.log('OnTick: Forcing reconnect after wake/visibility change (error state)...');
      this.attemptReconnect();
      return;
    }
    
    // Even if not in error state, connection might be stale after sleep/wake
    // Force reconnect proactively
    if (this.call) {
      console.log('OnTick: Proactive reconnect - cancelling existing connection...');
      try {
        this.call.cancel();
      } catch (e) {
        console.log('OnTick: Error cancelling call:', e);
      }
      this.isErrorConnect = true;
      setTimeout(() => {
        console.log('OnTick: Attempting reconnect after cancel...');
        this.attemptReconnect();
      }, 500);
    } else {
      console.log('OnTick: No active call, initiating fresh connection...');
      this.isErrorConnect = true;
      this.attemptReconnect();
    }
  }

  cleanup() {
    if (this.onlineListener) {
      window.removeEventListener('online', this.onlineListener);
    }
    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener);
    }
    if (this.resumeListener) {
      window.removeEventListener('resume', this.resumeListener);
    }
    if (this.focusListener) {
      window.removeEventListener('focus', this.focusListener);
    }
  }

  isOnline() {
    return navigator.onLine;
  }

  createSymbolClient() {
    const server = localStorage.getItem('trade.server');
    return new SymbolClient(serverApiConfig(server), null, null);
  }

  handleTickData(response) {
    const symbol = getGrpcResponse(response)[9];
    const tickData = {
      bid: getGrpcResponse(response)[1],
      ask: getGrpcResponse(response)[2],
      datetime_msc: getGrpcResponse(response)[5]
    };

    if (symbol === this.activeSymbol) {
      emitter.instance.emit('bar', response);
      this.dispatch(setOnTickReady(true));
    }

    this.dispatch(setBidAsk({
      bid: tickData.bid,
      ask: tickData.ask,
      symbol
    }));

    emitter.instance.emit('TickData', {
      [symbol]: tickData
    });
  }

  async handleError(
    request,
    metadata,
    resolution,
    onRealtimeCallback,
    lastBarsCache,
    previousSymbol
  ) {
    this.errorCount++;
    this.isErrorConnect = true;

    if (this.errorCount > TickService.RETRY_LIMIT) {
      console.error('Max retry attempts reached');
      this.reconnectParams = null;
      return;
    }

    // Store reconnection parameters (without metadata - will refresh on reconnect)
    this.reconnectParams = {
      request,
      resolution,
      onRealtimeCallback,
      lastBarsCache,
      previousSymbol
    };

    try {
      localStorage.setItem('ActiveChart', this.activeSymbol);
      await this.unsubscribeFromTick();

      // Check if online before attempting reconnection
      if (this.isOnline()) {
        console.log('Internet is online, attempting immediate reconnect');
        setTimeout(() => {
          this.attemptReconnect();
        }, TickService.RETRY_DELAY);
      } else {
        console.log('Internet is offline, waiting for connection to be restored...');
        // Will reconnect automatically when 'online' event fires
      }
    } catch (error) {
      console.error('Error during reconnection:', error);
    }
  }

  attemptReconnect() {
    if (!this.reconnectParams) {
      console.log('No reconnection parameters available');
      return;
    }

    const {
      request,
      resolution,
      onRealtimeCallback,
      lastBarsCache,
      previousSymbol
    } = this.reconnectParams;

    // Refresh metadata to get new token
    const freshMetadata = getMetadata();
    console.log('Reconnecting to OnTick with fresh metadata');
    
    this.onSubscribe(
      request,
      freshMetadata,
      resolution,
      onRealtimeCallback,
      this.activeSymbol,
      lastBarsCache,
      previousSymbol
    );
  }

  async unsubscribeFromTick() {
    this.call?.cancel();
    const unsubRequest = new SymbolName();
    unsubRequest.setName(this.activeSymbol);

    return new Promise((resolve, reject) => {
      this.symbolClient.tickUnsubscribe(unsubRequest, {}, (err, response) => {
        if (err || !response) {
          reject(err);
        } else {
          resolve();
        }
      });
    });
  }

  setupCallListeners(
    request,
    metadata,
    resolution,
    onRealtimeCallback,
    lastBarsCache,
    previousSymbol
  ) {
    this.call.on('data', (response) => {
      this.isErrorConnect = false;
      this.errorCount = 0; // Reset error count on successful connection
      this.reconnectParams = null; // Clear reconnect params
      this.handleTickData(response);
    });

    this.call.on('status', (status) => {
      // console.log('status', status)
      // Handle status updates if needed
    });

    this.call.on('error', (err) => {
      console.log('Connection error:', err)
      this.dispatch(setTradeEventStatus(false))
      this.handleError(
        request,
        metadata,
        resolution,
        onRealtimeCallback,
        lastBarsCache,
        previousSymbol
      );
    });
  }

  async subscribeToSymbol(symbol, metadata) {
    const request = new SymbolName();
    request.setName(symbol);

    return new Promise((resolve, reject) => {
      this.symbolClient.tickSubscribe(request, metadata, (err, response) => {
        if (err || !response) {
          reject(err);
        } else {
          resolve();
        }
      });
    });
  }

  async onSubscribe(
    request,
    metadata,
    resolution,
    onRealtimeCallback,
    activeSymbol,
    lastBarsCache,
    previousSymbol
  ) {
    this.activeSymbol = activeSymbol;

    // Always store params for proactive reconnect (update every time)
    this.reconnectParams = {
      request,
      resolution,
      onRealtimeCallback,
      lastBarsCache,
      previousSymbol
    };
    
    console.log('OnTick onSubscribe called for symbol:', activeSymbol, {
      hasCall: !!this.call,
      isErrorConnect: this.isErrorConnect
    });

    try {
      if (!this.call) {
        console.log('OnTick: Creating initial connection...');
        this.call = await this.symbolClient.onTick(request, metadata);
        this.setupCallListeners(
          request,
          metadata,
          resolution,
          onRealtimeCallback,
          lastBarsCache,
          previousSymbol
        );
        console.log('OnTick: Initial connection established for symbol:', activeSymbol);
        return;
      }

      if (this.isErrorConnect) {
        console.log('OnTick: Reconnecting after error...');
        const symbol = new SymbolName();
        symbol.setName(activeSymbol);
        const newRequest = new TickRequest();
        newRequest.addSymbols(symbol);
        this.call = await this.symbolClient.onTick(newRequest, metadata);
        this.isErrorConnect = false;
        this.setupCallListeners(
          newRequest,
          metadata,
          resolution,
          onRealtimeCallback,
          lastBarsCache,
          previousSymbol
        );
        console.log('OnTick: Reconnected and listeners set up successfully for symbol:', activeSymbol);
        return;
      }

      if (previousSymbol !== activeSymbol) {
        console.log('OnTick: Subscribing to new symbol:', activeSymbol);
        await this.subscribeToSymbol(activeSymbol, metadata);
      }
    } catch (error) {
      console.error('OnTick: Error in onSubscribe:', error);
      throw error;
    }
  }
}

const OnTick = () => {
  const dispatch = useAppDispatch();
  initializeWorker();

  const tickService = new TickService(dispatch);

  return {
    onSubscribe: tickService.onSubscribe.bind(tickService),
    cleanup: tickService.cleanup.bind(tickService)
  };
};

export default OnTick;